import { createContext, useCallback, useContext, useState, type ReactNode } from 'react'
import {
  useListFeaturesQuery,
  useListOrgsQuery,
  useGetOrgQuery,
  useGetOrgCapabilitiesQuery,
} from '@proaxiv/server-api'
import type { ListOrganizationsResponse, OrgCapabilities } from '@proaxiv/server-api'

const STORAGE_KEY = 'proaxiv.activeOrgId'

/** Per-feature descriptor returned by GET /api/v1/organizations/{orgId}/features */
interface FeatureInfo {
  id: string
  maxUsage: number
  currentUsage: number
}

/** Typed wrapper around the loosely-typed ListFeaturesApiResponse */
interface ListFeaturesData {
  success: boolean
  features: FeatureInfo[]
}

/** Safe defaults used while the capabilities request is in flight. */
const DEFAULT_ORG_CAPABILITIES: OrgCapabilities = {
  canUpdateOrg: false,
  canDeleteOrg: false,
  canInviteMembers: false,
  canSetFeature: false,
  canRequestPlanUpgrade: false,
}

/** Quota for the eventplanner feature (informational — backend is authoritative). */
export interface EventPlannerQuota {
  /** Admin-set maximum number of events; {@link Number.MAX_SAFE_INTEGER} means unlimited. */
  maxUsage: number
  /** Live count of events currently owned by the org (computed server-side). */
  currentUsage: number
}

interface ActiveOrgContextValue {
  /** The ID of the organization currently selected by the user. */
  activeOrgId: string | undefined
  /** Selects a new active organization and persists the choice to localStorage. */
  setActiveOrgId: (id: string) => void
  /**
   * Forces a fresh network fetch of the org list, bypassing RTK Query's normal
   * tag-invalidation path. Callers that just performed a mutation which adds the
   * caller to an org (e.g. accepting an invitation) should call this explicitly:
   * if the very first `listOrgs` request is still in flight when that mutation's
   * `invalidatesTags` fires, the invalidation has no query result to attach to yet
   * (tags are only recorded once a query fulfills) and is silently dropped, leaving
   * the list permanently stale until something else invalidates it later.
   */
  refetchOrgs?: () => void
  /** Feature IDs enabled for the active organization, fetched live from the API. */
  activeOrgFeatures: ReadonlyArray<string>
  /**
   * Whether {@link activeOrgFeatures} reflects a completed fetch for the current
   * `activeOrgId` yet. Feature-gated pages should wait for this to go `false` before
   * redirecting away, so a brief loading window isn't mistaken for "feature disabled".
   */
  isActiveOrgFeaturesLoading: boolean
  /**
   * Quota for the eventplanner feature, or {@code null} while loading / feature not enabled.
   * Used to show usage info and disable "New event" when at the limit.
   * The backend enforces the limit independently; this is a UI hint only.
   */
  eventPlannerQuota: EventPlannerQuota | null
  /**
   * Org-scoped capabilities pre-evaluated by the Drools engine for the active organization.
   * All flags default to {@code false} while the request is in flight or when no org is selected.
   * The backend enforces all permissions independently — these flags are informational UI hints.
   */
  activeOrgCapabilities: OrgCapabilities
}

const ActiveOrgContext = createContext<ActiveOrgContextValue | null>(null)

/**
 * Tracks which organization the user is currently navigating.
 * Must be rendered inside `<AuthProvider>` and `<Provider store={...}>` so it can
 * read `organizationIds` and call RTK Query hooks.
 *
 * The selected org ID is persisted to `localStorage` so it survives page reloads.
 * The stored value is validated with a direct `GET /organizations/{orgId}` lookup
 * (not by checking membership in a *paginated* org list — that list only returns a
 * default page of 20 orgs with no guaranteed ordering, so a valid stored org can easily
 * fall outside that page once enough orgs exist, incorrectly appearing "invalid" and
 * silently swapping the user into an unrelated org). Only when that direct lookup
 * confirms the stored org no longer exists / is no longer accessible do we fall back to
 * the first org in the list (e.g. after logout, leaving an org, or first-ever login with
 * nothing stored yet).
 *
 * Feature availability and ownership for the active org are fetched live from the
 * backend so that changes made by an admin are reflected without waiting for a token refresh.
 */
export function ActiveOrgProvider({ children }: { children: ReactNode }) {
  // Raw stored value — may be stale; validated below during render.
  const [storedOrgId, setStoredOrgId] = useState<string | undefined>(
    () => localStorage.getItem(STORAGE_KEY) ?? undefined
  )

  // Fetch the live org list — used only as a fallback source of "some org" when there's
  // no stored org yet, or the stored one turns out to be invalid (see below).
  const { data: orgsData, refetch: refetchOrgs } = useListOrgsQuery({})
  const orgIds =
    (orgsData as ListOrganizationsResponse | undefined)?.organizations?.map((o) => o.orgId) ?? []

  // Directly verify the stored org still exists and is accessible to the caller, instead
  // of checking membership in the (paginated, unordered) list above.
  const { isError: storedOrgIsInvalid } = useGetOrgQuery(
    { orgId: storedOrgId! },
    { skip: !storedOrgId }
  )

  // Derive the effective active org at render time (no effect required). Trust the stored
  // org optimistically while its validity check is in flight, to avoid flashing a different
  // org on every page load; only fall back once we have a confirmed rejection.
  const activeOrgId = storedOrgId && !storedOrgIsInvalid ? storedOrgId : orgIds[0]

  // Fetch the enabled features (+ quota) for the active org from the backend.
  const { data: featuresData, isFetching: isActiveOrgFeaturesLoading } = useListFeaturesQuery(
    { orgId: activeOrgId! },
    { skip: !activeOrgId }
  )
  const featuresList = (featuresData as ListFeaturesData | undefined)?.features ?? []
  const activeOrgFeatures: ReadonlyArray<string> = featuresList.map((f) => f.id)

  const eventPlannerFeature = featuresList.find((f) => f.id === 'eventplanner')
  const eventPlannerQuota: EventPlannerQuota | null = eventPlannerFeature
    ? {
        maxUsage:
          eventPlannerFeature.maxUsage >= 2_000_000_000
            ? Number.MAX_SAFE_INTEGER
            : eventPlannerFeature.maxUsage,
        currentUsage: eventPlannerFeature.currentUsage,
      }
    : null

  // Fetch org-scoped capabilities from the Drools engine for the active org.
  const { data: capsData } = useGetOrgCapabilitiesQuery(
    { orgId: activeOrgId! },
    { skip: !activeOrgId }
  )
  const activeOrgCapabilities: OrgCapabilities = capsData ?? DEFAULT_ORG_CAPABILITIES

  // Memoized so callers that put this in a useEffect/useCallback dependency array (e.g.
  // JoinOrgPage's post-join redirect timer) get a stable reference across re-renders —
  // an inline function here would change identity on every ActiveOrgProvider render
  // (which happens often, driven by its several RTK Query hooks) and could otherwise
  // cause such effects to keep re-firing and never actually complete their timer.
  const setActiveOrgId = useCallback((id: string) => {
    localStorage.setItem(STORAGE_KEY, id)
    setStoredOrgId(id)
  }, [])

  return (
    <ActiveOrgContext.Provider
      value={{
        activeOrgId,
        setActiveOrgId,
        refetchOrgs,
        activeOrgFeatures,
        isActiveOrgFeaturesLoading,
        eventPlannerQuota,
        activeOrgCapabilities,
      }}
    >
      {children}
    </ActiveOrgContext.Provider>
  )
}

/** Returns the active org context. Must be called inside `<ActiveOrgProvider>`. */
// eslint-disable-next-line react-refresh/only-export-components
export function useActiveOrg(): ActiveOrgContextValue {
  const ctx = useContext(ActiveOrgContext)
  if (!ctx) throw new Error('useActiveOrg must be used within <ActiveOrgProvider>')
  return ctx
}
